import { DicomMetadataStore } from '../services/DicomMetadataStore/DicomMetadataStore';
import api from './api';

function parseUIDsFromImageId(imageId) {
  if (!imageId || typeof imageId !== 'string') {
    return {};
  }

  const studyMatch = imageId.match(/\/studies\/([^/]+)/i);
  const seriesMatch = imageId.match(/\/series\/([^/]+)/i);
  const instanceMatch = imageId.match(/\/instances\/([^/]+)/i);

  return {
    StudyInstanceUID: studyMatch?.[1],
    SeriesInstanceUID: seriesMatch?.[1],
    SOPInstanceUID: instanceMatch?.[1],
  };
}

function normalizeUID(value) {
  if (value === null || value === undefined) {
    return undefined;
  }

  const trimmed = `${value}`.trim();
  if (!trimmed || trimmed.toLowerCase() === 'undefined' || trimmed.toLowerCase() === 'null') {
    return undefined;
  }

  return trimmed;
}


function resolveReferencedInstance({
  seriesMetadata,
  referencedImageId,
  sopInstanceUID,
  referenceStudyUID,
  referenceSeriesUID,
}) {
  const normalizedReferencedImageId = referencedImageId ? `${referencedImageId}`.trim() : undefined;
  const normalizedSOPInstanceUID = normalizeUID(sopInstanceUID);

  const seriesInstances = Array.isArray(seriesMetadata?.instances) ? seriesMetadata.instances : [];

  const instanceBySeriesImageId = normalizedReferencedImageId
    ? seriesInstances.find(instance => instance?.imageId === normalizedReferencedImageId)
    : undefined;

  const instanceBySeriesSOP = normalizedSOPInstanceUID
    ? seriesInstances.find(
        instance => normalizeUID(instance?.SOPInstanceUID) === normalizedSOPInstanceUID
      )
    : undefined;

  const instanceByStoreImageId = normalizedReferencedImageId
    ? DicomMetadataStore.getInstanceByImageId(normalizedReferencedImageId)
    : undefined;

  const fallbackInstance =
    instanceBySeriesImageId || instanceBySeriesSOP || instanceByStoreImageId || seriesInstances[0];

  return {
    StudyInstanceUID: normalizeUID(
      fallbackInstance?.StudyInstanceUID || seriesMetadata?.StudyInstanceUID || referenceStudyUID
    ),
    SeriesInstanceUID: normalizeUID(
      fallbackInstance?.SeriesInstanceUID || seriesMetadata?.SeriesInstanceUID || referenceSeriesUID
    ),
    SOPInstanceUID: normalizeUID(fallbackInstance?.SOPInstanceUID),
    FrameOfReferenceUID: normalizeUID(fallbackInstance?.FrameOfReferenceUID),
  };
}

function getProjectionAxesFromNormal(viewPlaneNormal) {
  const normalArray = Array.isArray(viewPlaneNormal)
    ? viewPlaneNormal
    : [viewPlaneNormal?.[0] ?? 0, viewPlaneNormal?.[1] ?? 0, viewPlaneNormal?.[2] ?? 1];
  const absNormal = normalArray.map(value => Math.abs(Number(value) || 0));
  const dominantAxis = absNormal.indexOf(Math.max(...absNormal));

  if (dominantAxis === 0) {
    return [1, 2];
  }

  if (dominantAxis === 1) {
    return [0, 2];
  }

  return [0, 1];
}

function computeAreaFromPolyline(points, viewPlaneNormal) {
  if (!Array.isArray(points) || points.length < 3) {
    return null;
  }

  const [axisA, axisB] = getProjectionAxesFromNormal(viewPlaneNormal);
  let area2 = 0;

  for (let i = 0; i < points.length; i++) {
    const current = points[i] || [];
    const next = points[(i + 1) % points.length] || [];
    const x1 = Number(current[axisA]) || 0;
    const y1 = Number(current[axisB]) || 0;
    const x2 = Number(next[axisA]) || 0;
    const y2 = Number(next[axisB]) || 0;
    area2 += x1 * y2 - x2 * y1;
  }

  return Math.abs(area2) / 2;
}

function buildFallbackTextBox(points) {
  const safePoint = Array.isArray(points?.[0]) ? points[0] : [0, 0, 0];

  return {
    hasMoved: false,
    worldPosition: [safePoint[0] || 0, safePoint[1] || 0, safePoint[2] || 0],
  };
}

function distance3D(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) {
    return 0;
  }

  const dx = (Number(a[0]) || 0) - (Number(b[0]) || 0);
  const dy = (Number(a[1]) || 0) - (Number(b[1]) || 0);
  const dz = (Number(a[2]) || 0) - (Number(b[2]) || 0);

  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

function validateMeasurementPayload(measurementObj) {
  const requiredFields = [
    ['SOPInstanceUID', measurementObj?.SOPInstanceUID],
    ['SeriesInstanceUID', measurementObj?.SeriesInstanceUID],
    ['StudyInstanceUID', measurementObj?.StudyInstanceUID],
  ];

  const missingFields = requiredFields
    .filter(([, value]) => !normalizeUID(value))
    .map(([field]) => field);

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
}

export default function addToVIDAADB(measurementData, measurementService, uiNotificationService) {
  if (measurementData.length === 0) {
    return;
  }

  console.log('Measurements to save:', measurementData);

  const promises = [];
  const skippedMeasurements = [];

  measurementData.forEach(measurement => {
    const { referenceStudyUID, referenceSeriesUID, getReport, uid, toolName } = measurement;

    const seriesMetadata = DicomMetadataStore.getSeries(referenceStudyUID, referenceSeriesUID);

    const commonRowItems = _getCommonRowItems(measurement, seriesMetadata);
    const label = measurement.label ? measurement.label : 'custom';

    // Geometry for standard linear/polyline measurements
    const measurementGeometry = {
      mode: 'polyline',
      line: {
        start: {
          x: measurement.points[0][0],
          y: measurement.points[0][1],
          z: measurement.points[0][2],
        },
        end: {
          x: measurement.points[1][0],
          y: measurement.points[1][1],
          z: measurement.points[1][2],
        },
      },
      polylinePoints: [],
    };

    // Metrics for standard linear/length measurements
    let measurementMetrics = {};

    if (getReport) {
      const report = getReport(measurement);

      measurementMetrics = {
        length: {
          value: report.values[report.columns.indexOf('Length')],
          units: report.values[report.columns.indexOf('Unit')],
        },
      };
    }

    const frameOfReferenceUID =
      measurement.FrameOfReferenceUID || measurement.metadata?.FrameOfReferenceUID;

    const viewPlaneNormal = measurement.metadata?.viewPlaneNormal || [0, 0, -1];

    const viewUp = measurement.metadata?.viewUp || [0, -1, 0];

    const referencedImageId = measurement.metadata?.referencedImageId;

    const parsedUIDs = parseUIDsFromImageId(referencedImageId);

    const sopInstanceUID =
      measurement.annotationSOPInstanceUID ||
      measurement.SOPInstanceUID ||
      measurement.metadata?.SOPInstanceUID ||
      parsedUIDs.SOPInstanceUID;

    const metadataFallbacks = resolveReferencedInstance({
      seriesMetadata,
      referencedImageId,
      sopInstanceUID,
      referenceStudyUID,
      referenceSeriesUID,
    });

    const normalizedSeriesInstanceUID =
      measurement.metadata?.SeriesInstanceUID ||
      referenceSeriesUID ||
      parsedUIDs.SeriesInstanceUID ||
      metadataFallbacks.SeriesInstanceUID;

    const normalizedStudyInstanceUID =
      measurement.metadata?.StudyInstanceUID ||
      referenceStudyUID ||
      parsedUIDs.StudyInstanceUID ||
      metadataFallbacks.StudyInstanceUID;

    const measurementObj = {
      id: measurement.uid,

      patient: commonRowItems ? commonRowItems['Patient ID'] : undefined,

      type: `${label}-polyline`,

      toolName,

      ...measurementGeometry,

      ...measurementMetrics,

      FrameOfReferenceUID: normalizeUID(
        frameOfReferenceUID || metadataFallbacks.FrameOfReferenceUID
      ),

      SOPInstanceUID: normalizeUID(sopInstanceUID || metadataFallbacks.SOPInstanceUID),

      camera: {
        viewPlaneNormal,
        viewUp,
      },

      normalVector: {
        x: viewPlaneNormal?.[0] ?? 0,
        y: viewPlaneNormal?.[1] ?? 0,
        z: viewPlaneNormal?.[2] ?? -1,
      },

      metadata: measurement.metadata,

      textBox:
        measurement.textBox ||
        measurement.annotationData?.handles?.textBox ||
        measurement.data?.handles?.textBox ||
        buildFallbackTextBox(measurement.points || []),

      SeriesInstanceUID: normalizeUID(
        seriesMetadata?.SeriesInstanceUID || normalizedSeriesInstanceUID
      ),

      StudyInstanceUID: normalizeUID(
        seriesMetadata?.StudyInstanceUID || normalizedStudyInstanceUID
      ),

      imageModality: seriesMetadata?.Modality,
    };

    if (measurementObj.metadata) {
      measurementObj.metadata = {
        ...measurementObj.metadata,

        SOPInstanceUID: normalizeUID(measurementObj.metadata.SOPInstanceUID || sopInstanceUID),

        SeriesInstanceUID: normalizeUID(
          measurementObj.metadata.SeriesInstanceUID || normalizedSeriesInstanceUID
        ),

        StudyInstanceUID: normalizeUID(
          measurementObj.metadata.StudyInstanceUID || normalizedStudyInstanceUID
        ),
      };
    }

    const validation = validateMeasurementPayload(measurementObj);

    if (!validation.isValid) {
      skippedMeasurements.push({
        uid: measurement.uid,
        toolName,
        missingFields: validation.missingFields,
        referencedImageId,
        parsedUIDs,
        metadataFallbacks,
      });

      console.error(
        `Skipping measurement ${measurement.uid}: missing required payload fields (${validation.missingFields.join(', ')})`,
        measurementObj
      );

      return;
    }

    console.log('Measurement ready for MongoDB transmission:', measurementObj);

    promises.push(saveMeasurement(measurementObj));
  });

  if (skippedMeasurements.length) {
    uiNotificationService?.show?.({
      title: 'Some Measurements Were Skipped',
      message: `Skipped ${skippedMeasurements.length} measurements with missing UIDs. Check console for details.`,
      type: 'error',
      duration: 7000,
    });
  }

  if (!promises.length) {
    return;
  }

  Promise.all(promises)
    .then(responses => {
      return responses.map(response => response.data);
    })
    .then(data => {
      console.log('Measurements saved successfully:', data);

      measurementData.forEach(measurement => {
        const existingMeasurement = measurementService.getMeasurement(measurement.uid);

        if (!existingMeasurement) {
          return;
        }

        const updatedMeasurement = {
          ...existingMeasurement,
          isSaved: true,
        };

        measurementService.update(measurement.uid, updatedMeasurement, false);
      });

      uiNotificationService?.show?.({
        title: 'Measurement Saved',
        message: `${data.length} measurements saved correctly.`,
        type: 'success',
        duration: 5000,
      });
    })
    .catch(error => {
      console.error('There was a problem saving the measurements:', error);

      uiNotificationService?.show?.({
        title: 'Error Saving Measurement',
        message: `There was a problem sending the measurements to the server. ${error}`,
        type: 'error',
        autoClose: false,
      });
    });
}

async function saveMeasurement(measurementData) {
  const path = '/annotations/image-annotation';

  // The api instance's request interceptor attaches Authorization/x-api-key from storage.
  return api.post(path, measurementData);
}

function _getCommonRowItems(measurement, seriesMetadata) {
  const firstInstance = seriesMetadata?.instances?.[0] || {};
  return {
    'Patient ID': firstInstance.PatientID,
    'Patient Name': firstInstance.PatientName?.Alphabetic || '',
    StudyInstanceUID: measurement.referenceStudyUID,
    SeriesInstanceUID: measurement.referenceSeriesUID,
    SOPInstanceUID: measurement.SOPInstanceUID || measurement.metadata?.SOPInstanceUID,
    Label: measurement.label || '',
  };
}
